import { Injectable, computed, inject, signal } from '@angular/core';
import { catchError, of, tap } from 'rxjs';

import { environment } from '../../environments/environment';
import {
  DamagedEquipmentDto,
  DashboardApiDto,
  EquipmentSummaryDto,
  FuneralUsageDto,
  IssueDto,
  LowStockAlertDto,
  OperationsSummaryDto,
  OverdueReturnDto,
  RecentActivityDto,
  StockSummaryDto,
} from '../models/api.models';
import { CaseUsageEntry } from '../models/report.models';
import {
  CheckoutRecord,
  DamagedEquipmentRecord,
} from '../models/equipment.models';
import { InventoryItem, StockIssue } from '../models/inventory.models';
import { ApiService } from './api.service';
import {
  asDict,
  findResultSetWithColumn,
  pick,
  pickDate,
  pickNumber,
  pickString,
} from '../utils/api-shape.util';

const EMPTY_STOCK: StockSummaryDto = {
  activeItems: 0,
  totalItems: 0,
  lowStock: 0,
  outOfStock: 0,
  totalInventoryValue: 0,
  pendingApprovals: 0,
  transactionsToday: 0,
  goodsReceivedToday: 0,
  issuesToday: 0,
};

const EMPTY_EQUIPMENT: EquipmentSummaryDto = {
  totalAssets: 0,
  available: 0,
  checkedOut: 0,
  reserved: 0,
  inRepair: 0,
  damaged: 0,
  lost: 0,
  dueToday: 0,
  overdue: 0,
  paidWaiting: 0,
};

const EMPTY_OPERATIONS: OperationsSummaryDto = {
  pendingReservations: 0,
  approvedReservations: 0,
  pendingTransfers: 0,
  openMaintenance: 0,
  inProgressMaintenance: 0,
  completedMaintenance: 0,
  upcomingCases: 0,
  completedCases: 0,
};

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private readonly api = inject(ApiService);

  private readonly dashboard = signal<DashboardApiDto | null>(null);
  private readonly loading = signal(false);
  private readonly error = signal<string | null>(null);

  readonly dashboardData = this.dashboard.asReadonly();
  readonly isLoading = this.loading.asReadonly();
  readonly loadError = this.error.asReadonly();

  readonly stockSummary = computed(
    () => this.dashboard()?.stock ?? EMPTY_STOCK,
  );
  readonly equipmentSummary = computed(
    () => this.dashboard()?.equipment ?? EMPTY_EQUIPMENT,
  );
  readonly operationsSummary = computed(
    () => this.dashboard()?.operations ?? EMPTY_OPERATIONS,
  );

  readonly inventoryStats = computed(() => {
    const stock = this.stockSummary();
    const data = this.dashboard();
    const active = stock.activeItems || data?.itemStats?.activeItems || 0;
    const total = stock.totalItems || data?.itemStats?.totalItems || active;
    return {
      totalItems: total,
      activeItems: active,
      lowStock: stock.lowStock || data?.lowStockCount || 0,
      discontinued: 0,
    };
  });

  readonly equipmentStats = computed(() => {
    const eq = this.equipmentSummary();
    const legacy = this.dashboard()?.equipmentStats;
    return {
      available: eq.available || legacy?.available || 0,
      checkedOut: eq.checkedOut || legacy?.checkedOut || 0,
      dueToday: eq.dueToday || legacy?.dueToday || 0,
      overdue: eq.overdue || this.dashboard()?.overdueCount || 0,
    };
  });

  readonly maintenanceStats = computed(() => {
    const ops = this.operationsSummary();
    return {
      totalJobs: ops.openMaintenance + ops.inProgressMaintenance,
      inProgress: ops.inProgressMaintenance,
      completed: ops.completedMaintenance,
      pending: ops.openMaintenance,
    };
  });

  readonly summary = computed(() => ({
    inventory: this.inventoryStats(),
    equipment: this.equipmentStats(),
    maintenance: this.maintenanceStats(),
    stock: this.stockSummary(),
    equipmentDetail: this.equipmentSummary(),
    operations: this.operationsSummary(),
    lowStockItems: this.lowStockItems(),
    overdueCheckouts: this.overdueCheckouts(),
    pendingJobs: this.dashboard()?.openMaintenanceJobs ?? [],
  }));

  readonly recentActivity = computed(
    () => this.dashboard()?.recentActivity ?? [],
  );

  readonly lowStockItems = computed<InventoryItem[]>(() =>
    (this.dashboard()?.lowStockAlerts ?? []).map((row) =>
      this.mapLowStock(row),
    ),
  );

  readonly overdueCheckouts = computed<CheckoutRecord[]>(() =>
    (this.dashboard()?.overdueReturns ?? []).map((row) => this.mapOverdue(row)),
  );

  readonly recentIssues = computed<StockIssue[]>(() =>
    (this.dashboard()?.recentIssues ?? []).map((row) => this.mapIssue(row)),
  );

  readonly recentCheckouts = computed<CheckoutRecord[]>(() =>
    (this.dashboard()?.recentCheckouts ?? []).map((row) =>
      this.mapCheckout(row),
    ),
  );

  readonly damagedEquipment = computed<DamagedEquipmentRecord[]>(() =>
    (this.dashboard()?.damagedEquipment ?? []).map((row) =>
      this.mapDamaged(row),
    ),
  );

  readonly caseUsage = computed<CaseUsageEntry[]>(() =>
    (this.dashboard()?.funeralUsage ?? []).map((row) => this.mapCaseUsage(row)),
  );

  readonly openMaintenanceJobs = computed(
    () => this.dashboard()?.openMaintenanceJobs ?? [],
  );

  readonly pendingApprovalsList = computed(
    () => this.dashboard()?.pendingApprovalsList ?? [],
  );

  load(): void {
    if (environment.useMockData) {
      return;
    }

    this.loading.set(true);
    this.error.set(null);

    this.api
      .get<unknown>('/Dashboard')
      .pipe(
        tap((data) => {
          this.dashboard.set(this.normalizeDashboard(data));
          this.loading.set(false);
        }),
        catchError(() => {
          this.loading.set(false);
          return of(null);
        }),
      )
      .subscribe();
  }

  private normalizeDashboard(payload: unknown): DashboardApiDto {
    const fromSets = this.fromResultSets(payload);
    const dict = asDict(payload) as DashboardApiDto;
    const data: DashboardApiDto = {
      ...fromSets,
      ...dict,
      stock: dict.stock ?? fromSets.stock,
      equipment: dict.equipment ?? fromSets.equipment,
      operations: dict.operations ?? fromSets.operations,
      recentActivity: dict.recentActivity?.length
        ? dict.recentActivity
        : fromSets.recentActivity,
      overdueReturns: dict.overdueReturns?.length
        ? dict.overdueReturns
        : fromSets.overdueReturns,
      recentIssues: dict.recentIssues?.length
        ? dict.recentIssues
        : fromSets.recentIssues,
      recentCheckouts: dict.recentCheckouts?.length
        ? dict.recentCheckouts
        : fromSets.recentCheckouts,
      lowStockAlerts: dict.lowStockAlerts?.length
        ? dict.lowStockAlerts
        : fromSets.lowStockAlerts,
      damagedEquipment: dict.damagedEquipment?.length
        ? dict.damagedEquipment
        : fromSets.damagedEquipment,
      funeralUsage: dict.funeralUsage?.length
        ? dict.funeralUsage
        : fromSets.funeralUsage,
      openMaintenanceJobs: dict.openMaintenanceJobs?.length
        ? dict.openMaintenanceJobs
        : fromSets.openMaintenanceJobs,
    };
    const stock = this.normalizeStock(data);
    const equipment = this.normalizeEquipment(data);
    const operations = this.normalizeOperations(data);

    return {
      itemStats: data.itemStats ?? {
        activeItems: stock.activeItems,
        totalItems: stock.totalItems,
      },
      equipmentStats: data.equipmentStats ?? {
        available: equipment.available,
        checkedOut: equipment.checkedOut,
        dueToday: equipment.dueToday,
      },
      stock,
      equipment,
      operations,
      totalAssets: data.totalAssets ?? equipment.totalAssets,
      overdueCount: data.overdueCount ?? equipment.overdue,
      damagedCount: data.damagedCount ?? equipment.damaged + equipment.lost,
      lowStockCount: data.lowStockCount ?? stock.lowStock,
      pendingApprovals: data.pendingApprovals ?? stock.pendingApprovals,
      recentActivity: (data.recentActivity ?? []).map((row) =>
        this.normalizeActivity(row),
      ),
      funeralUsage: data.funeralUsage ?? [],
      overdueReturns: data.overdueReturns ?? [],
      recentIssues: data.recentIssues ?? [],
      recentCheckouts: data.recentCheckouts ?? [],
      lowStockAlerts: data.lowStockAlerts ?? [],
      damagedEquipment: data.damagedEquipment ?? [],
      pendingApprovalsList: data.pendingApprovalsList ?? [],
      openMaintenanceJobs: data.openMaintenanceJobs ?? [],
      timestamp: data.timestamp,
    };
  }

  private fromResultSets(payload: unknown): Partial<DashboardApiDto> {
    const summary = asDict(pick(payload, 'summary', 'Summary'));
    // Gateway may return one flat SQL summary row (ActiveStockItems, …)
    // or nested stock/equipment/operations objects.
    const stockRow =
      (findResultSetWithColumn(
        payload,
        'GoodsReceivedToday',
        'ActiveStockItems',
        'ActiveItems',
        'LowStockItems',
        'LowStock',
      )[0] as Record<string, unknown> | undefined) ??
      (Object.keys(summary).length ? summary : undefined);
    const equipmentRow =
      (findResultSetWithColumn(
        payload,
        'AvailableEquipment',
        'CheckedOutEquipment',
        'EquipmentInRepair',
        'CheckedOut',
        'PaidWaiting',
      )[0] as Record<string, unknown> | undefined) ?? stockRow;
    const operationsRow =
      (findResultSetWithColumn(
        payload,
        'PendingReservations',
        'OpenMaintenanceJobs',
        'OpenMaintenance',
        'UpcomingCases',
      )[0] as Record<string, unknown> | undefined) ?? stockRow;

    const stock: StockSummaryDto | undefined = stockRow
      ? {
          activeItems: pickNumber(
            stockRow,
            'activeItems',
            'ActiveItems',
            'ActiveStockItems',
          ),
          totalItems: pickNumber(
            stockRow,
            'totalItems',
            'TotalItems',
            'TotalStockItems',
          ),
          lowStock: pickNumber(stockRow, 'lowStock', 'LowStock', 'LowStockItems'),
          outOfStock: pickNumber(
            stockRow,
            'outOfStock',
            'OutOfStock',
            'OutOfStockItems',
          ),
          totalInventoryValue: pickNumber(
            stockRow,
            'totalInventoryValue',
            'TotalInventoryValue',
          ),
          pendingApprovals: pickNumber(
            stockRow,
            'pendingApprovals',
            'PendingApprovals',
          ),
          transactionsToday: pickNumber(
            stockRow,
            'transactionsToday',
            'TransactionsToday',
            'TotalTransactionsToday',
          ),
          goodsReceivedToday: pickNumber(
            stockRow,
            'goodsReceivedToday',
            'GoodsReceivedToday',
          ),
          issuesToday: pickNumber(stockRow, 'issuesToday', 'IssuesToday'),
        }
      : undefined;

    const equipment: EquipmentSummaryDto | undefined = equipmentRow
      ? {
          totalAssets: pickNumber(
            equipmentRow,
            'totalAssets',
            'TotalAssets',
            'TotalEquipmentAssets',
          ),
          available: pickNumber(
            equipmentRow,
            'available',
            'Available',
            'AvailableEquipment',
          ),
          checkedOut: pickNumber(
            equipmentRow,
            'checkedOut',
            'CheckedOut',
            'CheckedOutEquipment',
          ),
          reserved: pickNumber(
            equipmentRow,
            'reserved',
            'Reserved',
            'ReservedEquipment',
          ),
          inRepair: pickNumber(
            equipmentRow,
            'inRepair',
            'InRepair',
            'EquipmentInRepair',
          ),
          damaged: pickNumber(
            equipmentRow,
            'damaged',
            'Damaged',
            'DamagedEquipment',
          ),
          lost: pickNumber(equipmentRow, 'lost', 'Lost', 'LostEquipment'),
          dueToday: pickNumber(
            equipmentRow,
            'dueToday',
            'DueToday',
            'EquipmentDueToday',
          ),
          overdue: pickNumber(
            equipmentRow,
            'overdue',
            'Overdue',
            'OverdueReturns',
          ),
          paidWaiting: pickNumber(
            equipmentRow,
            'paidWaiting',
            'PaidWaiting',
            'PaidWaitingCheckouts',
          ),
        }
      : undefined;

    const operations: OperationsSummaryDto | undefined = operationsRow
      ? {
          pendingReservations: pickNumber(
            operationsRow,
            'pendingReservations',
            'PendingReservations',
          ),
          approvedReservations: pickNumber(
            operationsRow,
            'approvedReservations',
            'ApprovedReservations',
          ),
          pendingTransfers: pickNumber(
            operationsRow,
            'pendingTransfers',
            'PendingTransfers',
          ),
          openMaintenance: pickNumber(
            operationsRow,
            'openMaintenance',
            'OpenMaintenance',
            'OpenMaintenanceJobs',
          ),
          inProgressMaintenance: pickNumber(
            operationsRow,
            'inProgressMaintenance',
            'InProgressMaintenance',
          ),
          completedMaintenance: pickNumber(
            operationsRow,
            'completedMaintenance',
            'CompletedMaintenance',
          ),
          upcomingCases: pickNumber(
            operationsRow,
            'upcomingCases',
            'UpcomingCases',
          ),
          completedCases: pickNumber(
            operationsRow,
            'completedCases',
            'CompletedCases',
          ),
        }
      : undefined;

    return {
      stock,
      equipment,
      operations,
      recentActivity: findResultSetWithColumn(
        payload,
        'ActivityType',
        'activityType',
      ) as RecentActivityDto[],
      overdueReturns: findResultSetWithColumn(
        payload,
        'DaysOverdue',
        'daysOverdue',
      ) as OverdueReturnDto[],
      recentIssues: findResultSetWithColumn(
        payload,
        'IssueNumber',
        'issueNumber',
      ) as IssueDto[],
      recentCheckouts: findResultSetWithColumn(
        payload,
        'CheckoutNumber',
        'checkoutNumber',
      ) as unknown as DashboardApiDto['recentCheckouts'],
      lowStockAlerts: findResultSetWithColumn(
        payload,
        'ReorderLevel',
        'reorderLevel',
      ) as LowStockAlertDto[],
      damagedEquipment: findResultSetWithColumn(
        payload,
        'DamageType',
        'damageType',
      ) as DamagedEquipmentDto[],
      funeralUsage: findResultSetWithColumn(
        payload,
        'FuneralCaseNumber',
        'FuneralCaseID',
        'FuneralRef',
        'funeralRef',
        'ItemsIssued',
      ) as FuneralUsageDto[],
    };
  }

  private normalizeStock(data: DashboardApiDto): StockSummaryDto {
    const raw = (data.stock ?? {}) as Record<string, unknown>;
    const legacy = data.itemStats;
    return {
      activeItems:
        pickNumber(raw, 'activeItems', 'ActiveItems') ||
        legacy?.activeItems ||
        0,
      totalItems:
        pickNumber(raw, 'totalItems', 'TotalItems') || legacy?.totalItems || 0,
      lowStock:
        pickNumber(raw, 'lowStock', 'LowStock') || data.lowStockCount || 0,
      outOfStock: pickNumber(raw, 'outOfStock', 'OutOfStock'),
      totalInventoryValue: pickNumber(
        raw,
        'totalInventoryValue',
        'TotalInventoryValue',
      ),
      pendingApprovals:
        pickNumber(raw, 'pendingApprovals', 'PendingApprovals') ||
        data.pendingApprovals ||
        0,
      transactionsToday: pickNumber(
        raw,
        'transactionsToday',
        'TransactionsToday',
      ),
      goodsReceivedToday: pickNumber(
        raw,
        'goodsReceivedToday',
        'GoodsReceivedToday',
      ),
      issuesToday: pickNumber(raw, 'issuesToday', 'IssuesToday'),
    };
  }

  private normalizeEquipment(data: DashboardApiDto): EquipmentSummaryDto {
    const raw = (data.equipment ?? {}) as Record<string, unknown>;
    const legacy = data.equipmentStats;
    return {
      totalAssets:
        pickNumber(raw, 'totalAssets', 'TotalAssets') || data.totalAssets || 0,
      available:
        pickNumber(raw, 'available', 'Available') || legacy?.available || 0,
      checkedOut:
        pickNumber(raw, 'checkedOut', 'CheckedOut') || legacy?.checkedOut || 0,
      reserved: pickNumber(
        raw,
        'reserved',
        'Reserved',
        'ReservedEquipment',
      ),
      inRepair: pickNumber(
        raw,
        'inRepair',
        'InRepair',
        'EquipmentInRepair',
      ),
      damaged: pickNumber(raw, 'damaged', 'Damaged', 'DamagedEquipment'),
      lost: pickNumber(raw, 'lost', 'Lost', 'LostEquipment'),
      dueToday:
        pickNumber(raw, 'dueToday', 'DueToday', 'EquipmentDueToday') ||
        legacy?.dueToday ||
        0,
      overdue:
        pickNumber(raw, 'overdue', 'Overdue', 'OverdueReturns') ||
        data.overdueCount ||
        0,
      paidWaiting: pickNumber(
        raw,
        'paidWaiting',
        'PaidWaiting',
        'PaidWaitingCheckouts',
      ),
    };
  }

  private normalizeOperations(data: DashboardApiDto): OperationsSummaryDto {
    const raw = (data.operations ?? {}) as Record<string, unknown>;
    return {
      pendingReservations: pickNumber(
        raw,
        'pendingReservations',
        'PendingReservations',
      ),
      approvedReservations: pickNumber(
        raw,
        'approvedReservations',
        'ApprovedReservations',
      ),
      pendingTransfers: pickNumber(raw, 'pendingTransfers', 'PendingTransfers'),
      openMaintenance: pickNumber(
        raw,
        'openMaintenance',
        'OpenMaintenance',
        'OpenMaintenanceJobs',
      ),
      inProgressMaintenance: pickNumber(
        raw,
        'inProgressMaintenance',
        'InProgressMaintenance',
      ),
      completedMaintenance: pickNumber(
        raw,
        'completedMaintenance',
        'CompletedMaintenance',
      ),
      upcomingCases: pickNumber(raw, 'upcomingCases', 'UpcomingCases'),
      completedCases: pickNumber(raw, 'completedCases', 'CompletedCases'),
    };
  }

  private normalizeActivity(
    row: RecentActivityDto | Record<string, unknown>,
  ): RecentActivityDto {
    const raw = row as Record<string, unknown>;
    return {
      id: pickNumber(raw, 'id', 'Id', 'entityId', 'EntityID'),
      activityType: pickString(raw, 'activityType', 'ActivityType'),
      reference: pickString(
        raw,
        'reference',
        'Reference',
        'referenceNo',
        'ReferenceNo',
      ),
      title: pickString(raw, 'title', 'Title'),
      description: pickString(raw, 'description', 'Description'),
      status: pickString(raw, 'status', 'Status'),
      activityDate: pickDate(raw, 'activityDate', 'ActivityDate'),
      itemCount: pickNumber(raw, 'itemCount', 'ItemCount'),
    };
  }

  private mapLowStock(row: LowStockAlertDto): InventoryItem {
    return {
      id: String(row.id),
      code: row.code || '',
      name: row.name || 'Unknown item',
      category: '',
      unit: 'Unit',
      stockLevel: row.stockLevel ?? 0,
      reorderLevel: row.reorderLevel ?? 0,
      price: 0,
      status: 'active',
    };
  }

  private mapOverdue(row: OverdueReturnDto): CheckoutRecord {
    return {
      id: String(row.id),
      checkoutNumber:
        pickString(row, 'checkoutNumber') ||
        pickString(row, 'equipment') ||
        `CHK-${row.id}`,
      customer: row.customer || '—',
      equipment:
        row.equipment || pickString(row, 'checkoutNumber') || `CHK-${row.id}`,
      checkoutDate: this.safeDate(row, 'checkoutDate'),
      dueDate: this.safeDate(row, 'dueDate'),
      status: 'overdue',
    };
  }

  private mapCheckout(row: {
    id: number;
    checkoutNumber?: string;
    equipment: string;
    customer: string;
    checkoutDate: string;
    dueDate: string;
    items?: number;
  }): CheckoutRecord {
    return {
      id: String(row.id),
      checkoutNumber: row.checkoutNumber || `CHK-${row.id}`,
      customer: row.customer || '—',
      equipment: row.equipment || row.checkoutNumber || `CHK-${row.id}`,
      checkoutDate: this.safeDate(row, 'checkoutDate'),
      dueDate: this.safeDate(row, 'dueDate'),
      status: 'active',
    };
  }

  private mapIssue(row: IssueDto): StockIssue {
    return {
      id: String(row.id),
      issueNumber: row.issueNumber || `ISS-${row.id}`,
      issuedTo: row.issuedTo || '—',
      branch: '',
      issueDate: this.safeDate(row, 'issueDate'),
      items: row.items || 0,
      status: 'issued',
    };
  }

  private mapDamaged(row: DamagedEquipmentDto): DamagedEquipmentRecord {
    const damageType = String(row.damageType || '')
      .toLowerCase()
      .includes('lost')
      ? 'lost'
      : 'damaged';
    const reported = this.safeDate(row, 'reportedDate');
    return {
      id: String(row.id),
      reportNumber: `DMG-${row.id}`,
      equipment: row.equipment || `Item ${row.id}`,
      reportedDate: reported || new Date().toISOString().slice(0, 10),
      damageType,
      status: 'open',
      estimatedCost: 0,
    };
  }

  private mapCaseUsage(row: FuneralUsageDto): CaseUsageEntry {
    const source = row as unknown as Record<string, unknown>;
    const id =
      pickNumber(source, 'FuneralCaseID', 'id') ||
      pickNumber(row, 'id') ||
      0;
    const caseRef =
      pickString(
        source,
        'FuneralCaseNumber',
        'funeralRef',
        'CaseRef',
        'caseRef',
      ) || (id ? `CASE-${id}` : 'CASE');
    const caseName =
      pickString(
        source,
        'DeceasedFullName',
        'ClientFullName',
        'caseName',
        'CaseName',
      ) || caseRef;
    const date =
      this.safeDate(source, 'FuneralDate') ||
      this.safeDate(source, 'date') ||
      this.safeDate(row, 'date');

    return {
      id: String(id || caseRef),
      caseRef,
      caseName,
      date,
      branch: pickString(source, 'BranchName', 'branch') || row.branch || '',
      itemsIssued:
        pickNumber(source, 'ItemsIssued', 'itemsIssued') ||
        row.itemsIssued ||
        0,
      totalValue:
        pickNumber(source, 'TotalValue', 'totalValue') ||
        pickNumber(row, 'totalValue'),
      caseType: pickString(source, 'FuneralType', 'caseType') || '',
      daysRemaining:
        pickNumber(source, 'DaysRemaining', 'daysRemaining') ||
        row.daysRemaining,
    };
  }

  private safeDate(row: unknown, key: string): string {
    const raw = pickDate(row, key);
    if (!raw) return '';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime()) || d.getFullYear() < 1900) return '';
    return raw;
  }
}
